import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  People as UsersIcon,
  Restaurant as RecipeIcon,
  Inventory as IngredientIcon,
  CalendarMonth as PlanIcon,
  CardMembership as MembershipIcon,
  Flag as FlagIcon,
  TrendingUp as ActiveIcon,
} from '@mui/icons-material';
import { getStats, AdminStats } from './adminApi';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color }) => (
  <Paper
    sx={{
      p: 3,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      borderLeft: `4px solid ${color}`,
    }}
  >
    <Box
      sx={{
        backgroundColor: `${color}20`,
        borderRadius: '50%',
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color,
        '& svg': { fontSize: 28 },
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h4" fontWeight="bold">
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Paper>
);

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getStats();
        setStats(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!stats) {
    return <Alert severity="warning">No stats available</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Overview of your meal planning platform
      </Typography>

      <Grid container spacing={3}>
        {/* Users */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Total Users"
            value={stats.users.total_users}
            subtitle={`${stats.users.new_users_week} this week`}
            icon={<UsersIcon />}
            color="#1976d2"
          />
        </Grid>

        {/* Active Users (WAU) — anyone with last_active_at in last 7 days,
            including free-tier browsers and paid users alike. Truer signal
            of app usage than Active Memberships. */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Active Users (7d)"
            value={stats.users.active_users_week}
            subtitle="opened the app this week"
            icon={<ActiveIcon />}
            color="#00897b"
          />
        </Grid>

        {/* Active Memberships */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Active Memberships"
            value={stats.memberships.active_memberships}
            subtitle={`${stats.memberships.auto_renew_count} auto-renew`}
            icon={<MembershipIcon />}
            color="#2e7d32"
          />
        </Grid>

        {/* Total Recipes */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Total Recipes"
            value={stats.recipes.total_recipes}
            subtitle={`${stats.recipes.stub_recipes} stubs`}
            icon={<RecipeIcon />}
            color="#ed6c02"
          />
        </Grid>

        {/* Flagged Recipes */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Flagged Recipes"
            value={stats.recipes.flagged_recipes}
            icon={<FlagIcon />}
            color="#d32f2f"
          />
        </Grid>

        {/* Ingredients */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Ingredients"
            value={stats.ingredients.total_ingredients}
            icon={<IngredientIcon />}
            color="#9c27b0"
          />
        </Grid>

        {/* Meal Plans */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Meal Plans"
            value={stats.mealPlans.total_plans}
            subtitle={`${stats.mealPlans.active_plans} active, ${stats.mealPlans.plans_created_week} created this week`}
            icon={<PlanIcon />}
            color="#0288d1"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
